import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { MODULES } from '@/shared/constants/modules';
import { API_BASE } from '@/shared/services/api';
import { ArrowRight, TrendingUp, Package, Warehouse as WarehouseIcon, DollarSign, Globe } from 'lucide-react';
import { useTenant } from '@/shared/contexts/TenantContext';
import { usePermissions } from '@/shared/contexts/PermissionContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const revenueData = [
  { month: 'Jan', revenue: 45000 },
  { month: 'Feb', revenue: 52000 },
  { month: 'Mar', revenue: 49000 },
  { month: 'Apr', revenue: 63000 },
  { month: 'May', revenue: 71000 },
  { month: 'Jun', revenue: 78000 }
];

const ordersData = [
  { month: 'Jan', ezparcel: 1200, wms: 3400 },
  { month: 'Feb', ezparcel: 1450, wms: 3800 },
  { month: 'Mar', ezparcel: 1350, wms: 4100 },
  { month: 'Apr', ezparcel: 1680, wms: 4500 },
  { month: 'May', ezparcel: 1890, wms: 4800 },
  { month: 'Jun', ezparcel: 2100, wms: 5200 }
];

export const ERPDashboard = () => {
  const { t } = useTranslation('common');
  const { activeContext } = useTenant();
  const { canAccessRoute } = usePermissions();
  const [dashboardData, setDashboardData] = useState(null);

  const canAccessModule = (module) => {
    const routes = [module.path, ...(module.subModules || []).map((subModule) => subModule.path)];
    return routes.some((route) => canAccessRoute(route));
  };

  const activeModules = Object.values(MODULES).filter((m) => m.enabled && m.id !== 'erp' && canAccessModule(m));

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const token = localStorage.getItem('ezerpToken');
        const response = await fetch(`${API_BASE}/api/erp/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          setDashboardData(await response.json());
        }
      } catch (error) {
        console.warn('ERP dashboard API unavailable', error);
      }
    };

    loadDashboard();
  }, []);

  const summary = dashboardData?.summary || {};
  const liveRevenueData = dashboardData?.trends?.revenue?.length ? dashboardData.trends.revenue : revenueData;
  const liveOrdersData = dashboardData?.trends?.volume_by_module?.length ? dashboardData.trends.volume_by_module : ordersData;
  const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const number = new Intl.NumberFormat('en-US');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t('dashboard.title', 'EzERP Dashboard')}</h1>
            <p className="text-muted-foreground mt-2 font-medium">
              {t('dashboard.subtitle', 'Unified Enterprise Resource Planning')} · <span className="text-green-600 font-bold uppercase tracking-tight">🚀 {t('dashboard.status', 'All Phases Live')}</span>
            </p>
          </div>
          
          <div className="h-12 w-[1px] bg-slate-200 dark:bg-slate-800 hidden md:block" />
          
          <Link to="/select-context">
            <Button variant="outline" className="h-12 border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold px-6 shadow-sm border-2 border-dashed hover:border-solid hover:border-primary transition-all rounded-xl">
              <Globe className="h-4 w-4 mr-2 text-primary" />
              {activeContext?.client_name ? `${t('common.switch', 'Switch')}: ${activeContext.client_name}` : t('common.selectClient', 'Select Client Context')}
            </Button>
          </Link>
        </div>
        
        <Badge variant="outline" className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-slate-50 border-slate-200">
          Master Baseline 2026
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card className="card-elevated border-none shadow-md bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider">{t('dashboard.metrics.totalRevenue', 'Total Revenue')}</p>
                <p className="text-2xl font-bold tracking-tight">{currency.format(summary.total_revenue ?? 0)}</p>
                <div className="flex items-center gap-1 text-xs text-green-600 font-bold mt-1">
                  <TrendingUp className="h-3 w-3" />
                  {dashboardData ? t('dashboard.metrics.liveApi', 'Live API data') : `+18.2% ${t('dashboard.metrics.vsLastMonth', 'vs last month')}`}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated border-none shadow-md bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider">{t('dashboard.metrics.activeShipments', 'Active Shipments')}</p>
                <p className="text-2xl font-bold tracking-tight">{number.format(summary.active_shipments ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1 font-medium italic">{t('dashboard.metrics.acrossAllModules', 'Across all active modules')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated border-none shadow-md bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                <WarehouseIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider">{t('dashboard.metrics.warehouseOrders', 'Warehouse Orders')}</p>
                <p className="text-2xl font-bold tracking-tight">{number.format(summary.warehouse_orders ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1 font-medium">{t('dashboard.metrics.realTimeProcessing', 'Real-time processing')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated border-none shadow-md bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                <Package className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider">{t('dashboard.metrics.deliveries', 'Deliveries')}</p>
                <p className="text-2xl font-bold tracking-tight">{number.format(summary.deliveries ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1 font-medium">{t('dashboard.metrics.monthlyThroughput', 'Monthly throughput')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="card-elevated border-none shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Revenue Performance Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={liveRevenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="hsl(var(--muted-foreground))" 
                  axisLine={false}
                  tickLine={false}
                  dy={10}
                />
                <YAxis 
                  stroke="hsl(var(--muted-foreground))" 
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="hsl(var(--success))"
                  strokeWidth={4}
                  dot={{ fill: 'hsl(var(--success))', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="card-elevated border-none shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <BarChart className="h-5 w-5 text-blue-600" />
              Volume by System Module
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={liveOrdersData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="hsl(var(--muted-foreground))" 
                  axisLine={false}
                  tickLine={false}
                  dy={10}
                />
                <YAxis 
                  stroke="hsl(var(--muted-foreground))" 
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="ezparcel" name="Logistics" fill="#dc2626" radius={[6, 6, 0, 0]} barSize={24} />
                <Bar dataKey="wms" name="Warehouse" fill="#2563eb" radius={[6, 6, 0, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Module Navigation */}
      <div>
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold tracking-tight font-['Space_Grotesk']">{t('dashboard.operationalEcosystem', 'Operational Ecosystem')}</h2>
          <Link to="/reports" className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 group">
            {t('dashboard.viewReports', 'View Dynamic Reports')} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {activeModules.map((module) => {
            const Icon = module.icon;
            
            return (
              <Card key={module.id} className="card-elevated border-none shadow-sm hover:shadow-xl transition-all duration-500 group overflow-hidden bg-white dark:bg-slate-900">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-8">
                    <div 
                      className="flex h-16 w-16 items-center justify-center rounded-2xl transition-all group-hover:scale-105 duration-500 group-hover:rotate-3 shadow-inner"
                      style={{ backgroundColor: `${module.color}10` }}
                    >
                      <Icon className="h-8 w-8" style={{ color: module.color }} />
                    </div>
                    {module.badge && (
                      <Badge variant="secondary" className="bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400 font-bold px-3 py-1">
                        {module.badge}
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-bold text-2xl mb-3 tracking-tight group-hover:text-blue-600 transition-colors">{module.name}</h3>
                  <p className="text-sm text-muted-foreground mb-8 leading-relaxed line-clamp-2 font-medium">{module.description}</p>
                  <Link to={module.path}>
                    <Button className="w-full py-6 bg-slate-50 hover:bg-slate-900 hover:text-white dark:bg-slate-800 dark:hover:bg-slate-100 dark:hover:text-slate-900 border-none shadow-none font-bold text-base transition-all duration-300 rounded-xl" variant="outline">
                      {t('common.launchConsole', 'Launch Console')}
                      <ArrowRight className="ml-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};
